import { useEffect, useRef, useState } from "react";
import { InfoWindow } from "@vis.gl/react-google-maps";
import { trpc } from "@/lib/trpc";
import { MapTooltipContent } from "@/components/map/MapTooltipContent";

const PIXEL_OFFSET: [number, number] = [0, -8];

interface MarkerData {
  id: string;
  name: string;
  ownership: string;
  confidence: number | null;
  address: string | null;
  sportTypes?: string[] | null;
}

interface PublicMapTooltipProps {
  token: string;
  locationId: string;
  anchor: google.maps.marker.AdvancedMarkerElement;
  markerData: MarkerData;
  onClose: () => void;
  onViewDetails: (locationId: string) => void;
}

export function PublicMapTooltip({
  token,
  locationId,
  anchor,
  markerData,
  onClose,
  onViewDetails,
}: PublicMapTooltipProps) {
  const screenshotCacheRef = useRef<Map<string, string>>(new Map());
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(
    screenshotCacheRef.current.get(locationId) ?? null,
  );
  const [screenshotLoading, setScreenshotLoading] = useState(
    !screenshotCacheRef.current.has(locationId),
  );

  const utils = trpc.useUtils();

  useEffect(() => {
    const cached = screenshotCacheRef.current.get(locationId);
    if (cached) {
      setScreenshotUrl(cached);
      setScreenshotLoading(false);
      return;
    }

    setScreenshotLoading(true);
    setScreenshotUrl(null);

    utils.share.getScreenshotUrl
      .fetch({ token, locationId })
      .then((result) => {
        screenshotCacheRef.current.set(locationId, result.url);
        setScreenshotUrl(result.url);
      })
      .catch(() => {
        setScreenshotUrl(null);
      })
      .finally(() => {
        setScreenshotLoading(false);
      });
  }, [locationId, token, utils]);

  return (
    <InfoWindow
      anchor={anchor}
      shouldFocus={false}
      headerDisabled
      pixelOffset={PIXEL_OFFSET}
    >
      <MapTooltipContent
        locationId={markerData.id}
        name={markerData.name}
        sportTypes={markerData.sportTypes ?? null}
        ownership={markerData.ownership}
        confidence={markerData.confidence}
        address={markerData.address}
        screenshotUrl={screenshotUrl}
        screenshotLoading={screenshotLoading}
        collections={null}
        onAddToCollection={() => {}}
        onClose={onClose}
        onViewDetails={onViewDetails}
      />
    </InfoWindow>
  );
}
